import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { toast } from "react-toastify";
import { actions } from "app/popleads/app/redux/appRedux";
import { post } from "app/popleads/networking/RequestService";
import { assistantDisplayName } from "./assistantIdentity";
import { keepSessionImage } from "./assistantSessionImages";
import { usageFrom } from "./assistantUsage";
import { commandsFor } from "./assistantCommands";
import {
    conversationTitle,
    deleteConversation,
    groupConversations,
    loadConversations,
    saveConversation,
} from "./assistantHistory";

/* A message that is mostly a screenshot is still a normal thing to send, so the cap is generous
   about count and conservative about size — five files is more than one turn needs, and 15MB is
   past what a screenshot or a one-page PDF ever weighs. */
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

/**
 * Which approval card the thread will draw is carried on the payload as `kind`, and the merge
 * approval predates the field — anything arriving without one that names no tool, or names
 * merge_profiles, is that. Any other tool the server would not run unasked (a read tool not yet on
 * mcpAutoApproveTools, say) is a plain "tool" confirmation; defaulted to "merge" it was drawn as
 * "Confirm this merge — Profile null". A kind the server sends is kept. Defaulted here rather than
 * at the card so the archived conversation carries the kind too, and reopening it a year from now
 * does not depend on the same fallback still being in the component.
 */
const approvalKindFor = (pending) => {
    if (pending.kind) return pending.kind;
    const tool = pending.tool || pending.toolName;
    return !tool || tool === "merge_profiles" ? "merge" : "tool";
};

const withApprovalKind = (pending) => (pending ? { ...pending, kind: approvalKindFor(pending) } : null);

/**
 * The user's offset from UTC in minutes, from the browser's own clock — east is positive, the
 * opposite sign of `getTimezoneOffset`. Sent with every turn because the assistant searches the
 * server log around times the user names ("at two o'clock"), and only the browser knows whose two
 * o'clock that is: the server sits in another time zone, and the user record's timezone cannot be
 * trusted.
 */
const clientUtcOffsetMinutes = () => -new Date().getTimezoneOffset();

/* What the backend said, not what axios said about it. A refusal the backend means the user to read
   — the daily limit, "sign out and sign in again" — comes back as a 400 with the sentence in
   `text`, and axios's own message for that is only "Request failed with status code 400". */
const failureText = (err) => err?.response?.data?.text || err?.message;

/* What a conversation started from "Ask the assistant" on an error opens with, as the user's turn. */
export const ERROR_QUESTION = "Why did I get this error?";

/**
 * The error a conversation is about, sent with every turn of it — the server rebuilds its note from
 * these on each question, since the history it gets is only the visible thread. Taken from the
 * thread itself, so a follow-up, a retry and a conversation reopened from the archive all carry it.
 */
export const errorParams = (messages) => {
    const asked = (messages || []).find((m) => m.role === "user" && m.errorContext);
    if (!asked) return {};
    const { requestId, context } = asked.errorContext;
    return {
        errorRequestId: requestId || undefined,
        errorContext: context ? JSON.stringify(context) : undefined,
    };
};

/**
 * Everything the assistant *is*, with nothing about how it looks.
 *
 * Two shells render it: the CRM's full-screen command palette, and the browser extension's panel.
 * They disagree about layout, chrome and keyboard shortcuts, and agree about all of this — the
 * thread, the request, the archive, and what a failure means. Extracted when the second shell
 * arrived, so a fix to the conversation is a fix in both products.
 *
 * Layout stays with the shells — refs, autoscroll, autogrow, portals, open/close — because those
 * are the parts that genuinely differ.
 *
 * `keepArchiveOpen` is the one layout choice made here: whether picking a conversation or starting a
 * new one leaves the conversations column where it is. The full-screen CRM window keeps it, the way
 * ChatGPT keeps its sidebar; the extension's narrow panel closes it to give the thread its width back.
 */
const useAssistantThread = ({ initialArchiveOpen = false, keepArchiveOpen = false } = {}) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { chatMessages } = useSelector((state) => state.app);

    const [messageInput, setMessageInput] = useState("");
    const [isAsking, setIsAsking] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [failedQuestion, setFailedQuestion] = useState(null);
    /* Whether the archive starts open is the shell's call, not the assistant's: the CRM palette has
       room for it as a standing column beside the thread, while the extension's 662px drawer can
       only show it as a sheet over the body — which must not be what greets you on open. */
    const [isArchiveOpen, setIsArchiveOpen] = useState(initialArchiveOpen);
    const [conversations, setConversations] = useState([]);
    const [conversationId, setConversationId] = useState(null);
    /* The id of the message whose approval is being answered, so that card's own buttons know it is
       theirs that went out. */
    const [approvingId, setApprovingId] = useState(null);
    /* The assistant is thinking from the moment a question or a decision goes out until its reply is
       in. A decision is a request like a question, and a question sent under it would race it: each
       reply writes the thread as it stood when its own request left, so whichever came back second
       wiped out the other. Everything that would start or replace a turn waits for this. */
    const isLoading = isAsking || approvingId !== null;
    /* Staged, not yet sent — cleared into the message the moment `sendMessage` runs. Kept as `File`
       objects rather than uploading them ahead of time: there is nowhere to attach them to until
       there is a question they belong to. */
    const [attachments, setAttachments] = useState([]);
    /* What `retry` resends. A failure must not lose the files along with the question — the
       question already survives it via `failedQuestion`, and an attachment is part of the same
       turn. */
    const [failedAttachments, setFailedAttachments] = useState([]);
    /* Today's usage against the daily limit, `{ used, limit }` in tokens — null until the backend
       has said. Every answer carries it after its own tokens are counted, so it only needs asking
       for once, when the window opens. */
    const [usage, setUsage] = useState(null);
    /* What the assistant may do: "errors" while its allowed tools only serve error help, which
       swaps the data suggestions for error ones. Comes with the usage, from the same request. */
    const [scope, setScope] = useState(null);
    const { commands, moreCommands } = commandsFor(scope);
    const refreshUsage = useCallback(async () => {
        try {
            const res = await post({ fn: "assistantUsage" });
            const today = usageFrom(res?.data);
            if (today) setUsage(today);
            setScope(res?.data?.scope || null);
        } catch (err) {
            /* A bar that does not show is no reason to bother the reader. */
        }
    }, []);

    const assistantName = assistantDisplayName(user);

    const greeting = useCallback(
        () => [
            {
                id: moment().valueOf(),
                role: "assistant",
                type: "greeting",
                content: `Hi ${user.name}, I am your assistant. How can I help you?`,
                timestamp: moment().valueOf(),
            },
        ],
        [user]
    );

    /**
     * One request, given the thread it should append its answer to. Split out from `sendMessage`
     * so a retry can re-ask without adding the question to the thread a second time.
     *
     * A failure leaves the thread exactly as it is. It used to drop the question back out, which
     * on a first question also took the whole panel back to the shortcut list — the screen
     * changing under you is a worse outcome than the failure itself.
     *
     * `files` rides on the same request as `files[]` — `post()` already builds `FormData`, so a
     * `File` object in the params is uploaded as-is, no separate upload step first.
     */
    const askAssistant = async (question, baseMessages, files = [], { newConversation = false } = {}) => {
        setIsAsking(true);
        setFailedQuestion(null);
        setFailedAttachments([]);
        setMessageInput("");

        const apiMessages = baseMessages
            .filter((m, i) => !(i === 0 && m.role === "assistant"))
            .map((m) => ({
                role: m.role === "assistant" ? "assistant" : "user",
                content: typeof m.content === "string" ? m.content : "",
            }));

        try {
            /* One call. The backend holds the LLM key, mints the user's MCP token and talks to the
               model, so nothing about the provider or the MCP server is known here any more — and
               the thread is sent in full each time because the connector on the other side is
               stateless. Expect this to take tens of seconds on a question that uses tools. */
            const res = await post({
                fn: "askAssistant",
                question,
                messages: JSON.stringify(apiMessages),
                files,
                clientUtcOffsetMinutes: clientUtcOffsetMinutes(),
                ...errorParams(baseMessages),
            });

            /* A turn can end in one of two ways. Usually an answer; sometimes a request for
               permission, because the model wants to run the one tool that deletes something. The
               second case has no answer yet and must not be read as an empty one. */
            const approval = withApprovalKind(res?.data?.pendingApproval);
            const answer = res?.data?.answer;
            const today = usageFrom(res?.data);
            if (today) setUsage(today);
            if (!answer && !approval) throw new Error("The assistant returned an empty answer.");

            const assistantResponse = {
                id: moment().valueOf(),
                role: "assistant",
                /* Explicit rather than inferred from `approval`/`content` at render time — the
                   refusal is the one case nothing else in the message shape reveals, since it is
                   plain text like any other answer and free to reword. `refused` comes from the
                   backend, not a match against that text. */
                /* "true"/"false" as a string from the backend, not a JSON boolean -- LUtil.json has
                   no live boolean overload (see WS.java). A plain truthy check here would read the
                   string "false" as true, since any non-empty string is truthy in JS -- that bug
                   shipped once already and tagged every answer as a refusal. */
                type: approval ? "approval_request" : res?.data?.refused === "true" ? "refusal" : "answer",
                content: answer || "",
                timestamp: moment().valueOf(),
                approval,
                responseId: res?.data?.responseId ?? null,
            };
            const complete = [...baseMessages, assistantResponse];
            dispatch(actions.setChatMessages(complete));

            /* Archived once the exchange is complete, not when the question is sent — an
               entry holding a question and no answer is not a conversation to come back to. An
               unanswered approval is the same thing: the turn is still open. */
            if (!approval) archive(complete, newConversation ? null : conversationId);
        } catch (err) {
            const errorResponse = {
                id: moment().valueOf(),
                role: "assistant",
                content: `⚠️ The assistant could not answer:\n${failureText(err)}`,
                timestamp: moment().valueOf(),
            };
            dispatch(actions.setChatMessages([...baseMessages, errorResponse]));
            setFailedQuestion(question);
            setFailedAttachments(files);
            toast("The assistant request failed — see the thread for details.", { type: "error" });
            /* The daily limit is one way to land here, and the bar should say so too. */
            refreshUsage();
        } finally {
            setIsAsking(false);
        }
    };

    /* One place that writes to the archive, because two paths now finish a turn: an answer, and an
       approval that has been decided. A thread with no user message yet (the bare greeting) is not
       worth archiving. Not awaited by its callers — a save failing must not hold up showing the
       answer, the same reasoning as the rest of this turn's error handling.
       Updates `conversations` from the save's own response rather than re-fetching the list — the
       save already returns the full saved row, and a list call here would re-download up to 30
       conversations' entire message histories on every single turn just to move one row to the
       top. `refreshConversations` (drawer open) is the only place a real list fetch still happens. */
    const archive = async (complete, id = conversationId) => {
        if (!complete.some((m) => m.role === "user")) return;
        try {
            const saved = await saveConversation({ id, messages: complete });
            if (!saved) return;
            setConversationId(saved.id);
            setConversations((prev) =>
                [saved, ...prev.filter((c) => c.id !== saved.id)]
                    .sort((a, b) => (b.updatedTime ?? 0) - (a.updatedTime ?? 0))
                    .slice(0, 30)
            );
        } catch (err) {
            toast("The conversation could not be saved.", { type: "error" });
        }
    };

    /**
     * Sends the user's yes or no on a pending tool call and lets the turn finish.
     *
     * No question and no history go with it: the server resumes from the response that asked, which
     * is why `responseId` was kept on the message. A refusal travels the same way as an approval —
     * the model is told no and answers to that, rather than the card just disappearing.
     *
     * The card is not taken away once it is answered. It is part of what was said: an irreversible
     * decision whose record vanishes on being made cannot be scrolled back to. So the decision is
     * written onto the approval, which is what stops the buttons rendering a second time, and the
     * answer arrives underneath as the next thing the assistant said. `responseId` is dropped with
     * it, because that turn is now closed on the provider's side.
     */
    const respondToApproval = async (message, approve) => {
        if (!message?.approval || message.approval.decision || isLoading) return;
        setApprovingId(message.id);

        try {
            const res = await post({
                fn: "askAssistant",
                responseId: message.responseId,
                approvalRequestId: message.approval.approvalRequestId,
                approve: approve ? 1 : 0,
                clientUtcOffsetMinutes: clientUtcOffsetMinutes(),
            });

            /* A declined card runs no tool, so the model's "not changed" reply is a turn with no tool
               call — and the backend replaces every such turn with its refusal ("I answer only from
               your PopLeads data…"), flagged `refused`. Right for a question, wrong under a card the
               reader has just declined, so the plain outcome is said instead. The flag, not the
               text, decides: the text is free to change. */
            const today = usageFrom(res?.data);
            if (today) setUsage(today);
            const declinedIntoRefusal = !approve && res?.data?.refused === "true";
            const answer =
                (!declinedIntoRefusal && res?.data?.answer) || (approve ? "Done." : "Cancelled — nothing was changed.");
            const decision = approve ? "approved" : "declined";
            /* { status: "ok"|"error"|"cancelled", at }, from the backend -- what actually happened,
               not just what was decided. Put on both messages: the approval card reads it to say
               whether the action it described really completed (see AssistantMergeApproval.js),
               and the result message carries its own copy for the same reason `type` does — so the
               archived record doesn't have to be reconstructed from prose. */
            const result = res?.data?.result ?? null;
            const complete = [
                ...chatMessages.map((m) =>
                    m.id === message.id ? { ...m, approval: { ...m.approval, decision }, result, responseId: null } : m
                ),
                {
                    id: moment().valueOf(),
                    role: "assistant",
                    type: "action_result",
                    content: answer,
                    result,
                    timestamp: moment().valueOf(),
                },
            ];
            dispatch(actions.setChatMessages(complete));
            archive(complete);
        } catch (err) {
            /* The card stays put on a failure. Clearing it would leave the user unable to retry a
               decision they have already made, on a turn the server still has open. */
            toast(`The decision could not be sent: ${failureText(err)}`, { type: "error" });
        } finally {
            setApprovingId(null);
        }
    };

    /**
     * A proposal the card found cannot go ahead, answered with its reason instead of a card.
     *
     * The message keeps its approval — marked `blocked`, which is what stops the card drawing — so
     * the archive still records what was asked. Its content becomes the reason, replacing the
     * model's line announcing the card ("I'll put it up for your approval"), which would otherwise
     * sit above a card that is not there.
     *
     * Nothing is sent. The provider's pending call is left unanswered, which costs nothing: the
     * next question goes out with the whole thread and no `responseId`, so the model reads this
     * reply as what it said. `responseId` is dropped for the same reason `respondToApproval` drops
     * it — this turn is closed.
     *
     * "Nothing was changed." is said in every case, because not every reason reads as a refusal
     * on its own — "that campaign is in the store, and cancelling it deletes every cart" is the why
     * and not the outcome.
     */
    const blockApproval = (message, reason) => {
        if (!message?.approval || message.approval.decision) return;
        const said = String(reason || "").trim();
        const sentence = /[.!?]$/.test(said) ? said : `${said}.`;
        const complete = chatMessages.map((m) =>
            m.id === message.id
                ? {
                      ...m,
                      type: "action_blocked",
                      content: `${sentence} Nothing was changed.`,
                      approval: { ...m.approval, decision: "blocked", blockedReason: reason },
                      responseId: null,
                  }
                : m
        );
        dispatch(actions.setChatMessages(complete));
        archive(complete);
    };

    /**
     * A write the reader confirmed and the CRM refused, told as what it was: tried, and not made.
     *
     * Unlike `blockApproval` the card stays, closed — it was read and confirmed, so it is part of
     * what was said — and the reason arrives underneath as the assistant's next message, rather
     * than in red on a card whose Apply was still live. Reported on a rename the server refused
     * because the name was taken.
     *
     * Nothing is sent, for `blockApproval`'s reason: the next question carries the whole thread.
     * "Nothing was changed." is true of every case that reaches here — the executor's refusals
     * send nothing, and `campaignEdit` rolls back on any exception.
     */
    const failApproval = (message, reason) => {
        if (!message?.approval || message.approval.decision) return;
        const said = String(reason || "").trim();
        const sentence = !said ? "" : /[.!?]$/.test(said) ? ` ${said}` : ` ${said}.`;
        const at = moment().valueOf();
        const complete = [
            ...chatMessages.map((m) =>
                m.id === message.id
                    ? { ...m, approval: { ...m.approval, decision: "failed", failedReason: said }, responseId: null }
                    : m
            ),
            {
                id: at,
                role: "assistant",
                type: "action_failed",
                content: `I tried to make this change, but it could not be made.${sentence} Nothing was changed.`,
                timestamp: at,
            },
        ];
        dispatch(actions.setChatMessages(complete));
        archive(complete);
    };

    /**
     * An exchange written straight into the thread, with nothing sent anywhere.
     *
     * Everything else here is a turn: a question goes out, an answer comes back. Undoing a
     * campaign write is not one. The rollback happens inside the approval card, through the
     * campaign module's own write path, and the model has no part in it — which is why the card
     * could say "Rolled back" while the conversation above it showed the change still standing.
     *
     * The thread is also the history: `askAssistant` sends it in full on the next question. So a
     * change that leaves no message behind is a change the model cannot know about, and asked "what
     * did we change?" a turn later it would describe the write it saw approved and stop there.
     * Putting the pair in the thread is therefore not decoration — it is how the rollback reaches
     * the next request.
     *
     * The user's side is written as the user, because the undo *was* the user's instruction; it
     * arrived as a button rather than as typing, and the thread has no other way to show a person
     * asking for something.
     *
     * Two ids, one millisecond apart. Every message here is keyed on `moment().valueOf()`, and two
     * created in the same tick would carry the same number — which is also the React key the
     * shells render them with.
     */
    const appendLocalTurn = (question, answer) => {
        const at = moment().valueOf();
        const complete = [
            ...chatMessages,
            { id: at, role: "user", content: question, timestamp: at },
            { id: at + 1, role: "assistant", content: answer, timestamp: at },
        ];
        dispatch(actions.setChatMessages(complete));
        archive(complete);
    };

    /**
     * Stages files for the next message rather than uploading them ahead of it — see `askAssistant`.
     * Silently caps rather than rejecting the whole batch: a drag of six files should attach five and
     * say so, not attach none.
     */
    const addAttachments = (fileList) => {
        const incoming = Array.from(fileList || []);
        if (incoming.length === 0) return;

        const room = MAX_ATTACHMENTS - attachments.length;
        if (room <= 0) {
            toast(`You can attach up to ${MAX_ATTACHMENTS} files to one message.`, { type: "warning" });
            return;
        }

        const accepted = [];
        const oversized = [];
        for (const file of incoming) {
            if (accepted.length >= room) break;
            if (file.size > MAX_ATTACHMENT_BYTES) {
                oversized.push(file.name);
                continue;
            }
            accepted.push({
                id: `${file.name}-${file.lastModified}-${file.size}`,
                file,
                name: file.name,
                size: file.size,
                type: file.type,
            });
        }

        if (oversized.length > 0) {
            toast(`${oversized.join(", ")} — over 15MB and was not attached.`, { type: "warning" });
        }
        if (incoming.length > room + oversized.length) {
            toast(`Only the first ${MAX_ATTACHMENTS} files were attached.`, { type: "warning" });
        }
        if (accepted.length > 0) setAttachments((prev) => [...prev, ...accepted]);
    };

    const removeAttachment = (id) => setAttachments((prev) => prev.filter((a) => a.id !== id));

    const clearAttachments = () => setAttachments([]);

    const sendMessage = (text = messageInput) => {
        const question = text.trim();
        /* Any text at all is a question, as it is in ChatGPT — "hi" and "Q3?" are both real ones. An
           attachment carries its own intent, so it needs no caption. */
        if ((question.length === 0 && attachments.length === 0) || isLoading) return;

        const files = attachments.map((a) => a.file);
        const updatedMessages = [
            ...chatMessages,
            {
                id: moment().valueOf(),
                role: "user",
                content: question,
                timestamp: moment().valueOf(),
                /* `imageId` lets the question show its picture for the rest of this page's life —
                   see assistantSessionImages. The file itself is never archived. */
                attachments: attachments.map((a) => {
                    const imageId = keepSessionImage(a.file);
                    return { name: a.name, size: a.size, type: a.type, ...(imageId ? { imageId } : {}) };
                }),
            },
        ];
        dispatch(actions.setChatMessages(updatedMessages));
        clearAttachments();
        askAssistant(question, updatedMessages, files);
    };

    /* The question is already the last thing in the thread, so this re-asks against it in place —
       its attachments too, from `failedAttachments`, since a failure must not have to be described
       to the assistant a second time by hand. */
    const retry = () => !isLoading && failedQuestion && askAssistant(failedQuestion, chatMessages, failedAttachments);

    const startNewChat = () => {
        if (isLoading) return;
        setMessageInput("");
        setActiveIndex(0);
        setFailedQuestion(null);
        setConversationId(null);
        if (!keepArchiveOpen) setIsArchiveOpen(false);
        clearAttachments();
        dispatch(actions.setChatMessages(greeting()));
    };

    /**
     * A new conversation about an error, asked at once: the button that starts it is the question.
     * `error` is `{ requestId, context }` from assistantErrorCapture, kept on the user's turn so the
     * thread shows what went with it and every later turn sends it again (see `errorParams`).
     *
     * The conversation it replaces is left as it was: the id passed down to the archive is the new
     * conversation's (none yet), because `conversationId` in this render is still the old one's and
     * saving under it would overwrite that conversation with this one.
     */
    const askAboutError = (error) => {
        if (isLoading || !error) return false;
        const at = moment().valueOf();
        const thread = [
            ...greeting(),
            { id: at + 1, role: "user", content: ERROR_QUESTION, timestamp: at, errorContext: error },
        ];
        setMessageInput("");
        setActiveIndex(0);
        setFailedQuestion(null);
        setConversationId(null);
        clearAttachments();
        dispatch(actions.setChatMessages(thread));
        askAssistant(ERROR_QUESTION, thread, [], { newConversation: true });
        return true;
    };

    /**
     * The conversation an earlier "Ask the assistant" started about this request, opened again rather
     * than asked a second time. The old CRM's link opens the same page on a reload or on Back, and
     * the answer belongs in the chat, not in a note on the page. The archive is where it lives — its
     * first question carries the error (see `askAboutError`) — so it is found from any tab or device.
     * With no such conversation, or no request id to look for, the error is asked about as new.
     */
    const reopenOrAskAboutError = async (error) => {
        const requestId = error?.requestId;
        if (requestId) {
            const archived = await loadConversations();
            setConversations(archived);
            const earlier = archived.find((c) =>
                (c.messages || []).some((m) => m.role === "user" && m.errorContext?.requestId === requestId)
            );
            if (earlier) {
                openConversation(earlier);
                return true;
            }
        }
        return askAboutError(error);
    };

    /* The archive already carries each conversation's full messages (see assistantHistory.js), so
       reopening one is synchronous — no fetch, no loading state on the row that was clicked. */
    const openConversation = (conversation) => {
        if (isLoading) return;
        setConversationId(conversation.id);
        setMessageInput("");
        setFailedQuestion(null);
        if (!keepArchiveOpen) setIsArchiveOpen(false);
        clearAttachments();
        dispatch(actions.setChatMessages(conversation.messages));
    };

    const removeConversation = async (event, conversation) => {
        event.stopPropagation();
        try {
            await deleteConversation(conversation.id);
            setConversations(await loadConversations());

            /* Deleting the conversation on screen leaves the thread with nothing behind it, so it
               goes back to being an unsaved one rather than silently re-saving on the next answer. */
            if (conversation.id === conversationId) setConversationId(null);
        } catch (err) {
            toast("The conversation could not be deleted.", { type: "error" });
        }
    };

    /* Called by a shell when it opens rather than run once on mount: the archive is now the
       backend's, and it can have been added to from another device since this component mounted. */
    const refreshConversations = useCallback(async () => setConversations(await loadConversations()), []);

    const query = messageInput.trim();
    const hasQuery = query.length > 0;
    /* What actually gates sending: any typed text, or an attachment that speaks for itself. Kept
       separate from `hasQuery` because that one also decides whether Up/Down still belong to the
       shortcut list, which an attachment with no typed text does not change. */
    const canSend = hasQuery || attachments.length > 0;

    /* Nothing has been asked yet, so the body is still the list of shortcuts. Once the thread holds
       a real exchange the list gives way to the answers — a change the send causes, not the typing. */
    const isBrowsing = chatMessages.length <= 1;
    const showCommands = isBrowsing && !isLoading;

    const runCommand = (index) => {
        const command = commands[index];
        if (command) sendMessage(command.label);
    };

    /**
     * Enter belongs to whatever the user is actually doing: to the text if there is any, to the
     * highlighted shortcut if the box is empty. Arrow keys move the highlight only while the box is
     * *also* empty — once there is a query, Up/Down go back to being the composer's own, so a
     * multi-line question can be navigated by caret rather than always jumping the shortcut list.
     */
    const handleKeyDown = (e) => {
        if (showCommands && !hasQuery && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            e.preventDefault();
            const step = e.key === "ArrowDown" ? 1 : -1;
            setActiveIndex((value) => (value + step + commands.length) % commands.length);
            return;
        }

        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (canSend) sendMessage();
            else if (showCommands) runCommand(activeIndex);
        }
    };

    useEffect(() => {
        dispatch(actions.setChatMessages(greeting()));
    }, [dispatch, greeting]);

    useEffect(() => {
        refreshUsage();
    }, [refreshUsage]);

    return {
        /* identity */
        assistantName,
        user,

        /* thread */
        messages: chatMessages,
        isLoading,
        failedQuestion,
        isBrowsing,
        retry,
        usage,

        /* approvals */
        approvingId,
        respondToApproval,
        blockApproval,
        failApproval,
        appendLocalTurn,

        /* composer */
        messageInput,
        setMessageInput,
        hasQuery,
        canSend,
        sendMessage,
        handleKeyDown,

        /* attachments */
        attachments,
        addAttachments,
        removeAttachment,

        /* commands */
        commands,
        moreCommands,
        showCommands,
        activeIndex,
        setActiveIndex,
        runCommand,

        /* archive */
        conversations,
        conversationGroups: groupConversations(conversations),
        conversationId,
        conversationTitle,
        isArchiveOpen,
        setIsArchiveOpen,
        refreshConversations,
        openConversation,
        removeConversation,
        startNewChat,

        /* errors */
        askAboutError,
        reopenOrAskAboutError,
    };
};

export default useAssistantThread;
